import className from "classnames";

// Shared tab styles (EvaluationAreaTabList, profile tabs redesign, etc.)
const SCROLL_CONTAINER_INNER =
  "tw:overflow-x-auto tw:px-4 tw:md:px-0 tw:[scrollbar-width:none] tw:[-ms-overflow-style:none] tw:[&::-webkit-scrollbar]:[display:none]";

/** Tab row scroller; z-10 keeps horizontal tabs above adjacent in-column content (e.g. profile layout). */
export const SCROLL_CONTAINER_CLASS = `tw:relative tw:z-10 ${SCROLL_CONTAINER_INNER}`;

/** Same scroller without z-index — use when tabs sit below `position: fixed` UI that shares a trapped ancestor stacking context (e.g. charity pinned header). */
export const SCROLL_CONTAINER_CLASS_NO_Z_INDEX = `tw:relative ${SCROLL_CONTAINER_INNER}`;

export const TAB_CLASSES_HEADLESS = [
  "tw:flex tw:items-center tw:justify-center tw:gap-2 tw:min-w-[140px] tw:shrink-0 tw:rounded-t-lg!",
  "tw:border-2! tw:border-gray-400! tw:border-b-night-800!",
  "tw:outline-none tw:whitespace-nowrap tw:transition-colors tw:duration-150 tw:ease-out",
  "tw:text-cn-dark-blue! tw:font-extrabold! tw:leading-6! tw:text-lg! tw:md:text-xl! tw:md:font-normal! tw:md:text-gray-700! tw:md:tracking-tight!",
  "tw:pt-3 tw:px-4 tw:pb-2 tw:md:px-6",
  "tw:hover:font-semibold tw:hover:text-night-900 tw:data-[selected]:hover:font-extrabold tw:data-[selected]:hover:text-cn-dark-blue",
  "tw:data-[selected]:font-extrabold! tw:data-[selected]:text-cn-dark-blue!",
  "tw:data-[selected]:border-2! tw:data-[selected]:border-x-night-800! tw:data-[selected]:border-t-night-800!",
  "tw:data-[selected]:border-b-2! tw:data-[selected]:border-b-transparent! tw:data-[selected]:bg-white!",
  "tw:data-[focus]:outline-none! tw:data-[focus]:ring-0! tw:data-[active]:outline-none! tw:data-[active]:ring-0!",
].join(" ");

export const TAB_BORDER_LINE_CLASSES =
  "tw:absolute! tw:bottom-0! tw:left-1/2! tw:-translate-x-1/2! tw:w-screen! tw:h-0.5! tw:bg-night-800! tw:md:left-0! tw:md:right-0! tw:md:w-auto! tw:md:translate-x-0!";

/** Headless UI Listbox used for tab navigation on small viewports (matches profile + beacon report tabs). */
const MOBILE_TAB_LISTBOX_BUTTON_CORE = [
  "tw:relative tw:w-full tw:inline-flex tw:justify-between tw:items-center",
  "tw:cursor-default tw:border-2 tw:border-gray-400 tw:rounded-lg",
  "tw:text-gray-700! tw:text-lg! tw:font-medium!",
  "tw:py-3! tw:px-4! tw:shadow-sm!",
  "tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:ring-offset-0",
].join(" ");

export const MOBILE_TAB_LISTBOX_BUTTON_CLASSES = `${MOBILE_TAB_LISTBOX_BUTTON_CORE} tw:bg-sand-100!`;

/** White surface (e.g. beacon evaluation area dropdown on charity report). */
export const MOBILE_TAB_LISTBOX_BUTTON_CLASSES_WHITE = `${MOBILE_TAB_LISTBOX_BUTTON_CORE} tw:bg-white!`;

const MOBILE_TAB_LISTBOX_OPTIONS_CORE =
  "tw:absolute tw:z-50 tw:mt-1 tw:w-full tw:rounded-lg tw:py-1 tw:text-base! tw:shadow-lg! tw:border-2! tw:border-gray-400! tw:focus:outline-none!";

export const MOBILE_TAB_LISTBOX_OPTIONS_CLASSES = `${MOBILE_TAB_LISTBOX_OPTIONS_CORE} tw:bg-sand-100!`;

export const MOBILE_TAB_LISTBOX_OPTIONS_CLASSES_WHITE = `${MOBILE_TAB_LISTBOX_OPTIONS_CORE} tw:bg-white!`;

export const MOBILE_TAB_LISTBOX_CHEVRON_WRAP_CLASSES =
  "tw:pointer-events-none tw:flex tw:items-center tw:justify-center tw:origin-center tw:transition-transform tw:duration-200 tw:ease-out";

export const MOBILE_TAB_LISTBOX_CHEVRON_ICON_CLASSES =
  "tw:size-5 tw:shrink-0 tw:text-gray-700";

/** Passed to Headless UI `Transition` wrapping mobile tab Listbox options. */
export const MOBILE_TAB_LISTBOX_TRANSITION_PROPS = {
  leave: "tw:transition tw:ease-in tw:duration-100",
  leaveFrom: "tw:opacity-100",
  leaveTo: "tw:opacity-0",
} as const;

export function mobileTabListboxOptionClassName({
  focus,
  selected,
}: {
  focus: boolean;
  selected: boolean;
}) {
  return className(
    "tw:relative tw:cursor-pointer tw:select-none tw:py-3 tw:pl-4 tw:pr-4 tw:text-lg",
    {
      "tw:font-semibold tw:text-cn-dark-blue tw:bg-brand-100 tw:border-l-4 tw:border-l-blue-600":
        selected,
      "tw:font-medium tw:text-gray-700": !selected,
      "tw:bg-gray-200": focus && !selected,
    },
  );
}
